import './normalize.css'
import '@utrecht/design-tokens/dist/index.css'

import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import VueTippy from 'vue-tippy'
import VueDebounce from 'vue-debounce'

import BasePage from './BasePage.vue'
import MapPage from './MapPage.vue'
import LinksPage from './LinksPage.vue'

const routes = [
    {
        path: '/erfgoed',
        component: MapPage,
        props: {
            settings: {
                title: 'Erfgoed',
                tableName: 'erfgoed_periodes',
                projection: 'EPSG:28992',
                fields: `
                    geometry
                    id
                    korte_beschrijving
                    periode
                    plaatje
                    thema
                    titel
                    verhaal
                    tags
                `,
                layers: [
                    {
                        type: 'WMTS',
                        name: 'achtergrondkaart',
                        url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:28992/{z}/{x}/{y}.png',
                        layer: 'brtachtergrondkaartgrijs',
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'school',
                        selectable: true,
                        clusterItems: true
                    },
                ],
                facets: [
                    {
                        title: 'Periode',
                        key: 'periode',
                        values: ['']
                    },
                    {
                        title: 'Thema',
                        key: 'thema',
                    },
                ],
                titleField: 'titel',
                detailFields: [
                    {
                        title: 'Periode',
                        key: 'periode',
                    },
                    {
                        title: 'Thema',
                        key: 'thema',
                    },
                    {
                        title: 'Tags',
                        key: 'tags',
                    },
                ],
                features: {
                    listDisplay: true,
                    filter: true,
                },
            },
        },
    },
    {
        path: '/basisrapportages',
        component: MapPage,
        props: {
            settings: {
                title: 'Erfgoed',
                tableName: 'erfgoed_basisrapportages',
                projection: 'EPSG:28992',
                fields: `
                    begin_periode
                    code_nieuw
                    code_oud
                    eind_periode
                    geometry
                    id
                    link
                    literatuur
                    nr
                    samenvatting
                    periode
                    titel
                `,
                layers: [
                    {
                        type: 'WMTS',
                        name: 'achtergrondkaart',
                        url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:28992/{z}/{x}/{y}.png',
                        layer: 'brtachtergrondkaartgrijs',
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'school',
                        selectable: true,
                        clusterItems: true
                    },
                ],
                facets: [
                    {
                        title: 'Periode',
                        key: 'periode',
                    },
                ],
                titleField: 'titel',
                detailFields: [
                    {
                        title: 'Nr',
                        key: 'nr',
                    },
                    {
                        title: 'Begin Periode',
                        key: 'begin_periode',
                    },
                    {
                        title: 'Eind Periode',
                        key: 'eind_periode',
                    },
                    {
                        title: 'Periode',
                        key: 'periode',
                    },
                    {
                        title: 'Code nieuw',
                        key: 'code_nieuw',
                    },
                ],
                features: {
                    listDisplay: false,
                    filter: true,
                },
            },
        },
    },
    {
        path: '/overlast',
        component: MapPage,
        props: {
            settings: {
                title: 'Overlast',
                tableName: 'overlast',
                projection: 'EPSG:3857',
                fields: `
                    id
                    geometry
                    naam
                    adres
                    initiatiefnemer
                    type_project
                    start_uitvoering
                    eind_uitvoering
                    korte_beschrijving
                    beschrijving
                    website
                `,
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'school',
                        selectable: true,
                    },
                ],
                facets: [
                    {
                        title: 'Periode',
                        key: 'periode',
                    },
                    {
                        title: 'Thema',
                        key: 'thema',
                    },
                    {
                        title: 'Tags',
                        key: 'tags',
                    },
                ],
                titleField: 'naam',
                descriptionField: 'adres',
                detailFields: [
                    {
                        title: 'Vanaf',
                        key: 'start_uitvoering',
                    },
                    {
                        title: 'Tot',
                        key: 'eind_uitvoering',
                    },
                    {
                        title: 'Wie',
                        key: 'initiatiefnemer',
                    },
                ],
                features: {
                    listDisplay: true,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/projecten',
        component: MapPage,
        props: {
            settings: {
                title: 'Projecten',
                tableName: 'bouwprojecten',
                projection: 'EPSG:3857',
                styleName: 'projecten',
                url: 'https://www.utrecht.nl/projects.json',
                urlProjection: 'EPSG:4326',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'projecten',
                        selectable: true,
                        clusterItems: true
                    },
                ],
                facets: [
                    {
                        title: 'Wijk',
                        key: 'areas',
                    },
                    {
                        title: 'Projecttype',
                        key: 'categories',
                    },
                ],
                titleField: 'title',
                descriptionField: 'description',
                detailFields: [],
                features: {
                    listDisplay: true,
                    legendDisplay: true,
                    filter: true,
                },
                legend: {
                    items: [
                        {
                            'label': 'Verkeersprojecten',
                            'icon': 'images/marker-verkeer.svg'
                        },
                        {
                            'label': 'Bouwprojecten',
                            'icon': 'images/marker-bouw.svg'
                        },
                        {
                            'label': 'Groenprojecten',
                            'icon': 'images/marker-groen.svg'
                        },
                        {
                            'label': 'Energieprojecten',
                            'icon': 'images/marker-energie.svg'
                        }
                    ]
                }
            },
        },
    },
    {
        path: '/impactondernemers',
        component: MapPage,
        props: {
            settings: {
                title: 'Impactondernemers',
                tableName: 'impactondernemers',
                styleName: 'impactondernemers',
                url: 'https://datastore.utrecht.delta10.cloud/api/v1/datasets/impactondernemers',
                projection: 'EPSG:3857',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'impactondernemers',
                        selectable: true,
                        clusterItems: true
                    },
                ],
                facets: [
                    {
                        title: 'Type ondernemer',
                        key: 'type_ondernemer',
                    }
                ],
                titleField: 'naam',
                descriptionField: 'adres',
                detailFields: [
                    {
                        title: 'Type ondernemer',
                        key: 'type_ondernemer',
                    },
                ],
                features: {
                    listDisplay: true,
                    filter: true,
                },
            },
        },
    },
    {
        path: '/hubs',
        component: MapPage,
        props: {
            settings: {
                title: 'Hubs',
                tableName: 'hubs',
                styleName: 'hubs',
                url: 'https://datastore.utrecht.delta10.cloud/api/v1/datasets/hubs-pvh',
                projection: 'EPSG:28992',
                layers: [
                    {
                        type: 'WMTS',
                        name: 'achtergrondkaart',
                        url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:28992/{z}/{x}/{y}.png',
                        layer: 'brtachtergrondkaartgrijs',
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'hubs',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'title',
                descriptionField: null,
                detailFields: [],
                features: {
                    listDisplay: true,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/deelmobiliteit',
        component: MapPage,
        props: {
            settings: {
                title: 'Deelmobiliteit',
                tableName: 'deelmobiliteit',
                styleName: 'deelmobiliteit',
                projection: 'EPSG:3857',
                url: '/data/deelmobiliteit.geojson',
                urlProjection: 'EPSG:4326',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'deelmobiliteit',
                        selectable: true,
                        clusterItems: true,
                    },
                ],
                facets: [
                    {
                        title: 'Vervoersmiddel',
                        key: 'Vervoersmiddel',
                    },
                    {
                        title: 'Aanbieder',
                        key: 'Aanbieder',
                    },
                ],
                titleField: 'Vervoersmiddel',
                descriptionField: 'Adresindicatie',
                detailFields: [
                    {
                        title: 'Aanbieder',
                        key: 'Aanbieder',
                    }
                ],
                features: {
                    listDisplay: false,
                    filter: true,
                },
            },
        },
    },
    {
        path: '/wijken-buurten',
        component: MapPage,
        props: {
            settings: {
                title: 'Wijken en Buurten',
                tableName: 'bouwprojecten',
                projection: 'EPSG:3857',
                styleName: 'projecten',
                url: 'https://www.utrecht.nl/projects.json',
                urlProjection: 'EPSG:4326',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'school',
                        selectable: true,
                    },
                ],
                facets: [
                    {
                        title: 'Wijk',
                        key: 'areas',
                    },
                    {
                        title: 'Projecttype',
                        key: 'categories',
                    },
                ],
                titleField: 'title',
                descriptionField: 'description',
                detailFields: [],
                features: {
                    listDisplay: true,
                    legendDisplay: true,
                    filter: true,
                },
                legend: {
                    items: [
                        {
                            'label': 'Verkeersprojecten',
                            'icon': 'images/marker-verkeer.svg'
                        },
                        {
                            'label': 'Bouwprojecten',
                            'icon': 'images/marker-bouw.svg'
                        },
                        {
                            'label': 'Groenprojecten',
                            'icon': 'images/marker-groen.svg'
                        },
                        {
                            'label': 'Energieprojecten',
                            'icon': 'images/marker-energie.svg'
                        }
                    ]
                }
            },
        },
    },
    {
        path: '/verkeershinder',
        component: MapPage,
        props: {
            settings: {
                title: 'Verkeershinder',
                tableName: 'verkeershinder',
                styleName: 'verkeershinder',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.delta10.cloud/api/v1/datasets/verkeershinder',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'verkeershinder',
                        selectable: true,
                    },
                ],
                facets: [
                    {
                        title: 'Wijk',
                        key: 'wijk',
                    }
                ],
                titleField: 'titel',
                descriptionField: 'wat',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [
                    {
                        title: 'Begin werkzaamheden',
                        key: 'begin_werkzaamheden',
                    },
                    {
                        title: 'Eind werkzaamheden',
                        key: 'eind_werkzaamheden',
                    },
                    {
                        title: 'Wijk',
                        key: 'wijk',
                    }
                ],
                features: {
                    listDisplay: true,
                    filter: true,
                },
            },
        },
    },
    {
        path: '/singelroute',
        component: MapPage,
        props: {
            settings: {
                title: 'Singelroute',
                tableName: 'singelroute',
                styleName: 'singelroute-punten',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/singelroute-punten',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'singelroute-lijn',
                        styleName: 'singelroute-lijn',
                        url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/singelroute-lijn',
                        selectable: false,
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'singelroute-punten',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'titel',
                descriptionField: 'tekst',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [],
                features: {
                    listDisplay: false,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/moreelse-route',
        component: MapPage,
        props: {
            settings: {
                title: 'Moreelse route',
                tableName: 'moreelse route',
                styleName: 'moreelse-route-punten',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/moreelse-route-punten',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'moreelse-route-lijn',
                        styleName: 'moreelse-route-lijn',
                        url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/moreelse-route-lijn',
                        selectable: false,
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'moreelse-route-punten',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'titel',
                descriptionField: 'tekst',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [],
                features: {
                    listDisplay: false,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/vredenburgroute',
        component: MapPage,
        props: {
            settings: {
                title: 'Vredenburg',
                tableName: 'vredenburgroute',
                styleName: 'moreelse-route-punten',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/vredenburgroute-punten',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'vredenburgroute-lijn',
                        styleName: 'moreelse-route-lijn',
                        url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/vredenburgroute-lijn',
                        selectable: false,
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'moreelse-route-punten',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'titel',
                descriptionField: 'tekst',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [],
                features: {
                    listDisplay: false,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/vredenburgroute',
        component: MapPage,
        props: {
            settings: {
                title: 'Vredenburgroute',
                tableName: 'vredenburgroute',
                styleName: 'moreelse-route-punten',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/vredenburgroute-punten',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'vredenburgroute-lijn',
                        styleName: 'moreelse-route-lijn',
                        url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/vredenburgroute-lijn',
                        selectable: false,
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'moreelse-route-punten',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'titel',
                descriptionField: 'tekst',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [],
                features: {
                    listDisplay: false,
                    filter: false,
                },
            },
        },
    },
    {
        path: '/veeartsenijroute',
        component: MapPage,
        props: {
            settings: {
                title: 'Veeartsenijroute',
                tableName: 'veeartsenijroute',
                styleName: 'moreelse-route-punten',
                projection: 'EPSG:3857',
                url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/veeartsenijroute-punten',
                urlProjection: 'EPSG:28992',
                layers: [
                    {
                        type: 'MAPLIBRE',
                        options: {
                            style: 'https://api.maptiler.com/maps/basic/style.json?key=K3FQAjXPERToZYyHKNPG',
                        },
                    },
                    {
                        type: 'VECTOR',
                        name: 'veeartsenijroute-lijn',
                        styleName: 'moreelse-route-lijn',
                        url: 'https://datastore.utrecht.onatlas.nl/api/v1/datasets/veeartsenijroute-lijn',
                        selectable: false,
                    },
                    {
                        type: 'VECTOR',
                        name: 'schools',
                        styleName: 'moreelse-route-punten',
                        selectable: true,
                    },
                ],
                facets: [],
                titleField: 'titel',
                descriptionField: 'tekst',
                detailLinkText: 'Bekijk meer informatie',
                detailFields: [],
                features: {
                    listDisplay: false,
                    filter: false,
                },
            },
        },
    },
    { path: '/', component: LinksPage },
]

const router = createRouter({
    history: createWebHistory(),
    routes,
})

const app = createApp(BasePage)
app.use(router)
app.use(VueDebounce)
app.use(
    VueTippy,
    // optional
    {
        directive: 'tippy', // => v-tippy
        component: 'tippy', // => <tippy/>
        componentSingleton: 'tippy-singleton', // => <tippy-singleton/>
        distance: 5,
        placement: 'top',
        duration: [200, 175],
        hideOnClick: true,
        interactive: true,
        ignoreAttributes: true,
        allowHTML: false,
        boundary: 'viewport',
        delay: [1000, 0],
        theme: 'dark',
    }
)

app.mount('#app')
